import type { AuthSessionInfo } from '@/app/api/auth/_lib/types';
import Bugsnag from '@bugsnag/js';

/** Browser client for the modern BFF session. It never receives OAuth tokens. */

const BFF_REQUEST_TIMEOUT_MS = 10_000;

export class AuthSessionApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'AuthSessionApiError';
    this.status = status;
    this.code = code;
  }
}

interface AuthSessionReportContext {
  source: string;
  operation: string;
}

export function reportAuthSessionFailure(
  error: unknown,
  context: AuthSessionReportContext,
): void {
  const apiError = error instanceof AuthSessionApiError ? error : null;
  // A logged-out session is expected. A 403 from a first-party BFF request is
  // anomalous (for example, a proxy modifying request headers), so retain it
  // as a warning for operators without treating it as a paging error.
  if (apiError?.status === 401) return;

  const metadata = {
    source: context.source,
    operation: context.operation,
    status: apiError?.status,
    code: apiError?.code,
    originalErrorClass: error instanceof Error ? error.name : typeof error,
  };
  if (typeof window !== 'undefined' && Bugsnag.isStarted()) {
    // Use a local generic error so a future server response can never make
    // its way into Bugsnag's error message.
    Bugsnag.notify(new Error('Modern auth BFF request failed'), (event) => {
      event.severity = apiError?.status === 0 || apiError?.status === 403 ? 'warning' : 'error';
      event.addMetadata('authBff', metadata);
    });
  }

  console.error('auth.bff browser failure', metadata);
}

async function coarseError(response: Response): Promise<{ code?: string; message: string }> {
  try {
    const body = (await response.json()) as {
      error?: unknown;
      error_description?: unknown;
    };
    return {
      code: typeof body.error === 'string' ? body.error : undefined,
      message:
        typeof body.error_description === 'string'
          ? body.error_description
          : 'Authentication is temporarily unavailable.',
    };
  } catch {
    return { message: 'Authentication is temporarily unavailable.' };
  }
}

async function bffFetch(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(path, { ...init, signal: AbortSignal.timeout(BFF_REQUEST_TIMEOUT_MS) });
  } catch (error) {
    const timedOut = error instanceof Error && error.name === 'TimeoutError';
    throw new AuthSessionApiError(
      timedOut
        ? 'Authentication took too long. Please try again.'
        : 'Unable to reach authentication. Please try again.',
      0,
      timedOut ? 'request_timeout' : 'network_error',
    );
  }
}

async function responseJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new AuthSessionApiError('The authentication response was invalid.', response.status);
  }
}

function validSession(value: unknown): value is AuthSessionInfo {
  const session = value as Partial<AuthSessionInfo> | null;
  return Boolean(
    session &&
      session.authenticated === true &&
      session.user &&
      typeof session.user.id === 'number' &&
      typeof session.user.email === 'string' &&
      typeof session.expiresAt === 'number',
  );
}

export async function getAuthSession(): Promise<AuthSessionInfo | null> {
  const response = await bffFetch('/api/auth/session', {
    method: 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (response.status === 401) return null;
  if (!response.ok) {
    const error = await coarseError(response);
    throw new AuthSessionApiError(error.message, response.status, error.code);
  }
  const session = await responseJson(response);
  if (!validSession(session)) {
    throw new AuthSessionApiError('The session response was invalid.', response.status);
  }
  return session;
}

export async function startEmbeddedAuth(returnTo: string = '/'): Promise<string> {
  const response = await bffFetch('/api/auth/embedded/start', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ return_to: returnTo }),
  });
  if (!response.ok) {
    const error = await coarseError(response);
    throw new AuthSessionApiError(error.message, response.status, error.code);
  }
  const body = (await responseJson(response)) as { interaction_token?: unknown };
  if (typeof body.interaction_token !== 'string' || !body.interaction_token) {
    throw new AuthSessionApiError('The authentication response was invalid.', response.status);
  }
  return body.interaction_token;
}

export async function completeEmbeddedAuth(resumeUrl: string): Promise<AuthSessionInfo> {
  const response = await bffFetch('/api/auth/embedded/complete', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ resume_url: resumeUrl }),
  });
  if (!response.ok) {
    const error = await coarseError(response);
    throw new AuthSessionApiError(error.message, response.status, error.code);
  }
  const session = await responseJson(response);
  if (!validSession(session)) {
    throw new AuthSessionApiError('The session response was invalid.', response.status);
  }
  return session;
}
